import { useCallback, useEffect, useState } from 'react'
import { formatDateTime, translate, type MessageKey } from '../../configuration/localization'
import type { ConsentAction } from '../../domain/privacy/consent'
import { privacyPreferencesApi, type ConsentPreference } from './application'

function statusLabel(status: ConsentPreference['status']): string {
  return translate(`privacy.status.${status}` as MessageKey)
}

export function DataAiPreferences({ tenantId }: { tenantId: string }) {
  const [preferences, setPreferences] = useState<ConsentPreference[]>([])
  const [reason, setReason] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    const result = await privacyPreferencesApi.load(tenantId)
    if (result.error) {
      setError(result.error.message)
      setPreferences([])
    } else {
      setPreferences(result.data ?? [])
    }
    setLoading(false)
  }, [tenantId])

  useEffect(() => { void load() }, [load])

  async function decide(preference: ConsentPreference, action: ConsentAction) {
    setLoading(true)
    setError('')
    setMessage('')
    const result = await privacyPreferencesApi.decide({
      tenantId,
      purpose: preference.purpose,
      category: preference.category,
      policyVersion: preference.policyVersion,
      action,
      reason,
    })
    if (result.error) {
      setError(result.error.message)
    } else {
      setMessage(translate('privacy.updated'))
      setReason('')
      await load()
    }
    setLoading(false)
  }

  return <section id="data-ai-preferences" className="panel">
    <div className="section-heading">
      <div><p className="eyebrow">{translate('privacy.eyebrow')}</p><h2>{translate('privacy.title')}</h2></div>
      <p className="muted">{translate('privacy.subtitle')}</p>
    </div>

    {error && <div className="notice error" role="alert">{error}</div>}
    {message && <div className="notice" role="status">{message}</div>}

    <label>{translate('privacy.reason')}
      <textarea value={reason} onChange={(event) => setReason(event.target.value)} rows={2} placeholder={translate('privacy.reasonPlaceholder')} />
    </label>
    <p className="muted">{translate('privacy.revokeHelp')}</p>

    <div className="two-column">
      {preferences.map((preference) => <article key={`${preference.purpose}:${preference.category}:${preference.policyVersion}`} className="panel compact-panel">
        <p className="eyebrow">{preference.category} · v{preference.policyVersion}</p>
        <h3>{translate(preference.labelKey as MessageKey)}</h3>
        <p>{translate(preference.descriptionKey as MessageKey)}</p>
        <p><strong>{translate('privacy.currentStatus')}:</strong> {statusLabel(preference.status)}</p>
        {preference.effectiveAt && <p className="muted">{translate('privacy.effectiveAt')}: {formatDateTime(preference.effectiveAt)}</p>}
        <div className="top-actions">
          <button type="button" disabled={loading || reason.trim().length < 2 || preference.status === 'granted'} onClick={() => void decide(preference, 'grant')}>{translate('privacy.grant')}</button>
          <button type="button" disabled={loading || reason.trim().length < 2 || preference.status === 'denied'} onClick={() => void decide(preference, 'deny')}>{translate('privacy.deny')}</button>
          <button type="button" disabled={loading || reason.trim().length < 2 || preference.status !== 'granted'} onClick={() => void decide(preference, 'revoke')}>{translate('privacy.revoke')}</button>
        </div>
      </article>)}
    </div>
    {!loading && preferences.length === 0 && <p className="muted">{translate('privacy.noPolicies')}</p>}
  </section>
}
